/**
 * "Existing layer" — the third way to add data, Esri Content's own idea:
 * a layer already built once is a thing to reuse, not something to import
 * again from scratch every time it is needed in a new map.
 *
 * Two lists, from two different places:
 *   - "My layers" costs no request at all — every module the signed-in user
 *     can see was already fetched at sign-in (see `moduleService`), so this
 *     is a plain filter of what is already in memory.
 *   - "Shared with me" is a real request: those modules are not otherwise
 *     visible to this user, only the one layer inside each that its owner
 *     shared (see `layerLibraryService.fetchSharedWithMe`).
 *
 * Either way, picking a candidate re-fetches it FULL before handing it to
 * the caller — see `layerLibraryService.fetchLayer`'s own doc comment on why
 * the summary shown in this list is never enough to copy from directly.
 */

import { useEffect, useState } from 'react';
import { Check, Layers as LayersIcon, Loader2, User as UserIcon } from 'lucide-react';
import type { Layer } from '../../types';
import {
  layerLibraryService,
  type OwnLayerEntry,
  type SharedLayerEntry,
} from '../../services/layerLibraryService';
import { getLayerDisplayName } from '../../layers/capabilities';

/**
 * A rough source label for the picker list — not the full
 * `getLayerCapabilities` classification, which needs the OWNING module (for
 * its ArcGIS/GeoServer URL) to tell a FeatureServer from a MapServer or WMS.
 * That module is never fetched just to label a candidate; this is honest
 * about the coarser thing it can tell from the layer alone.
 */
function sourceLabel(layer: Layer): string {
  if (layer.localRaster) return 'Local raster (GeoTIFF)';
  if (layer.localData) return 'Local file';
  if (layer.arcgisLayerId !== undefined) return 'ArcGIS Server';
  return 'GeoServer WMS';
}

interface Candidate {
  key: string;
  moduleId: string;
  moduleTitle: string;
  ownerName?: string;
  layer: Layer;
}

function Row({
  candidate,
  busy,
  onAdd,
}: {
  candidate: Candidate;
  busy: boolean;
  onAdd: () => void;
}) {
  return (
    <li className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
        <LayersIcon className="h-4 w-4 text-slate-400" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-slate-800">
          {getLayerDisplayName(candidate.layer)}
        </span>
        <span className="mt-0.5 block truncate text-[11px] text-slate-400">
          {sourceLabel(candidate.layer)} · from {candidate.moduleTitle}
          {candidate.ownerName ? ` · shared by ${candidate.ownerName}` : ''}
        </span>
      </span>
      <button
        type="button"
        onClick={onAdd}
        disabled={busy}
        className="gl-btn gl-btn--secondary flex-shrink-0 px-2.5 py-1.5 text-[11.5px]"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        Add
      </button>
    </li>
  );
}

export default function ExistingLayerPicker({
  currentModuleId,
  currentUserId,
  onAdd,
}: {
  currentModuleId: string;
  currentUserId: string | undefined;
  /** Called once the full layer (features included) has been fetched. */
  onAdd: (layer: Layer) => void;
}) {
  const [mine, setMine] = useState<OwnLayerEntry[]>([]);
  const [shared, setShared] = useState<SharedLayerEntry[] | null>(null);
  const [sharedError, setSharedError] = useState<string | null>(null);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setMine(layerLibraryService.listMyLayers(currentUserId, currentModuleId));
  }, [currentUserId, currentModuleId]);

  useEffect(() => {
    let active = true;
    // Directly shared and group-shared read as the same thing here — both
    // are "a layer I may copy into my own map, which I did not otherwise
    // have access to" — so they land in the same list rather than a
    // separate section for a distinction this picker has no use for.
    void Promise.all([layerLibraryService.fetchSharedWithMe(), layerLibraryService.fetchSharedWithMyGroups()])
      .then(([direct, viaGroup]) => {
        if (active) setShared([...direct, ...viaGroup]);
      })
      .catch(() => {
        if (active) setSharedError('Could not load layers shared with you.');
      });
    return () => {
      active = false;
    };
  }, []);

  const candidates: Candidate[] = [
    ...mine.map((entry) => ({
      key: `own|${entry.moduleId}|${entry.layer.id}`,
      moduleId: entry.moduleId,
      moduleTitle: entry.moduleTitle,
      layer: entry.layer,
    })),
    ...(shared ?? []).map((entry) => ({
      key: `shared|${entry.moduleId}|${entry.layer.id}`,
      moduleId: entry.moduleId,
      moduleTitle: entry.moduleTitle,
      ownerName: entry.ownerName,
      layer: entry.layer,
    })),
  ];

  const term = query.trim().toLowerCase();
  const filtered = term
    ? candidates.filter(
        (entry) =>
          getLayerDisplayName(entry.layer).toLowerCase().includes(term) ||
          entry.moduleTitle.toLowerCase().includes(term),
      )
    : candidates;

  const add = async (candidate: Candidate) => {
    setAddingKey(candidate.key);
    setAddError(null);
    try {
      const layer = await layerLibraryService.fetchLayer(candidate.moduleId, candidate.layer.id);
      onAdd(layer);
    } catch {
      setAddError(`Could not load "${getLayerDisplayName(candidate.layer)}". It may have been removed.`);
    } finally {
      setAddingKey(null);
    }
  };

  const loadingShared = shared === null && !sharedError;

  return (
    <div className="space-y-3">
      <p className="text-[11.5px] leading-relaxed text-slate-500">
        Add a copy of a layer you already built in another application, or one another owner shared with
        you. Changing this copy will not change the original.
      </p>

      {candidates.length > 4 && (
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search layers…"
          className="gl-input w-full px-2.5 py-1.5 text-[12.5px]"
        />
      )}

      {addError && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[11.5px] text-red-600">
          {addError}
        </p>
      )}

      {loadingShared && mine.length === 0 ? (
        <p className="flex items-center gap-2 py-6 text-center text-xs text-slate-400">
          <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : filtered.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-xs text-slate-400">
          {candidates.length === 0
            ? 'No other layers to reuse yet — layers you build in other applications, or that someone shares with you, will show up here.'
            : `No layers match “${query}”.`}
        </p>
      ) : (
        <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-0.5">
          {filtered.map((candidate) => (
            <Row
              key={candidate.key}
              candidate={candidate}
              busy={addingKey === candidate.key}
              onAdd={() => void add(candidate)}
            />
          ))}
        </ul>
      )}

      {sharedError && (
        <p className="flex items-center gap-1.5 text-[10.5px] text-slate-400">
          <UserIcon className="h-3 w-3" /> {sharedError}
        </p>
      )}
    </div>
  );
}
